import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const OpportunityCard = ({ opportunity, onViewDetails, onEdit, onDelete }) => {
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'qualified': return 'bg-primary/10 text-primary border border-primary/20';
      case 'proposal': return 'bg-warning/10 text-warning border border-warning/20';
      case 'negotiation': return 'bg-accent/10 text-accent border border-accent/20';
      case 'won': return 'bg-success/10 text-success border border-success/20';
      case 'lost': return 'bg-error/10 text-error border border-error/20';
      default: return 'bg-muted text-muted-foreground border border-border';
    }
  };

  const getRoleColor = (role) => {
    return role === 'Lead' ?'bg-primary/10 text-primary border border-primary/20' :'bg-slate-100 text-slate-600 border border-slate-200';
  };

  const getOwnershipBadge = (ownedBy) => {
    if (ownedBy === 'Partner') {
      return { label: 'Partner-Led', icon: 'Users', className: 'bg-purple-50 text-purple-700 border border-purple-200' };
    }
    return { label: 'eCity-Led', icon: 'Building2', className: 'bg-blue-50 text-blue-700 border border-blue-200' };
  };

  const ownershipBadge = getOwnershipBadge(opportunity?.ownedBy);

  const handleViewProfile = (e) => {
    e?.stopPropagation();
    navigate(`/engagement-management-hub/opportunity/${opportunity?.id}`, { state: { opportunity } });
  };

  const handleEdit = (e) => {
    e?.stopPropagation();
    onEdit?.(opportunity);
  };

  const handleDeleteClick = (e) => {
    e?.stopPropagation();
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = (e) => {
    e?.stopPropagation();
    setShowDeleteConfirm(false);
    onDelete?.(opportunity);
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 relative flex flex-col">
      {/* Header Row: Title + Action Icons */}
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-base font-bold text-foreground leading-snug pr-3 flex-1 min-w-0">
          {opportunity?.name}
        </h3>
        {/* Action Icons — compact, right-aligned */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={handleViewProfile}
            title="View Profile"
            className="p-1.5 rounded-md text-slate-400 hover:text-primary hover:bg-primary/10 transition-all"
          >
            <Icon name="ExternalLink" size={15} />
          </button>
          <button
            onClick={handleEdit}
            title="Edit"
            className="p-1.5 rounded-md text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-all"
          >
            <Icon name="Pencil" size={15} />
          </button>
          <button
            onClick={handleDeleteClick}
            title="Delete"
            className="p-1.5 rounded-md text-slate-400 hover:text-error hover:bg-error/10 transition-all"
          >
            <Icon name="Trash2" size={15} />
          </button>
        </div>
      </div>

      {/* Status + Role + Ownership pills */}
      <div className="flex flex-wrap items-center gap-1.5 mb-4">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${getStatusColor(opportunity?.status)}`}>
          {opportunity?.status}
        </span>
        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${getRoleColor(opportunity?.role)}`}>
          {opportunity?.role}
        </span>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${ownershipBadge?.className}`}>
          <Icon name={ownershipBadge?.icon} size={11} />
          {ownershipBadge?.label}
        </span>
      </div>

      {/* Divider */}
      <div className="border-t border-border/60 mb-4" />

      {/* Data Rows */}
      <div className="space-y-2.5 mb-4 flex-1">
        <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
          <Icon name="Building2" size={15} className="flex-shrink-0 text-slate-400" />
          <span className="truncate font-medium text-foreground/80">{opportunity?.sector}</span>
        </div>
        <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
          <Icon name="Banknote" size={15} className="flex-shrink-0 text-slate-400" />
          <span className="font-semibold text-foreground">{opportunity?.value}</span>
        </div>
        <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
          <Icon name="Calendar" size={15} className="flex-shrink-0 text-slate-400" />
          <span>{opportunity?.expectedClose}</span>
        </div>
      </div>

      {/* Win Probability */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-medium text-muted-foreground">Win Probability</span>
          <span className="text-sm font-bold text-foreground">{opportunity?.winProbability}%</span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-300"
            style={{ width: `${opportunity?.winProbability}%` }}
          />
        </div>
      </div>

      {/* CTA */}
      <Button
        variant="outline"
        size="sm"
        iconName="Eye"
        iconPosition="left"
        iconSize={15}
        onClick={() => onViewDetails(opportunity)}
        fullWidth
      >
        View Details
      </Button>

      {/* Delete confirmation overlay */}
      {showDeleteConfirm && (
        <div
          className="absolute inset-0 bg-white/96 backdrop-blur-sm rounded-xl z-10 flex flex-col items-center justify-center gap-3 p-6"
          onClick={(e) => e?.stopPropagation()}
        >
          <div className="p-3 bg-error/10 rounded-full">
            <Icon name="Trash2" size={22} className="text-error" />
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold text-foreground mb-1">Delete Opportunity?</p>
            <p className="text-xs text-muted-foreground">"{opportunity?.name}" will be permanently removed.</p>
          </div>
          <div className="flex gap-2 w-full">
            <button
              onClick={(e) => { e?.stopPropagation(); setShowDeleteConfirm(false); }}
              className="flex-1 px-3 py-2 text-xs font-medium border border-border rounded-lg hover:bg-muted transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteConfirm}
              className="flex-1 px-3 py-2 text-xs font-medium bg-error text-white rounded-lg hover:bg-error/90 transition-all"
            >
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpportunityCard;